import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Trash2, Clock, ArrowLeft, Users } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminWaitlistPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);

  const fetchWaitlist = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/waitlist`);
      setEntries(data);
    } catch (e) {
      console.error("Failed to load waitlist", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== "admin") {
      navigate("/");
      return;
    }
    fetchWaitlist();
  }, [user, authLoading, navigate, fetchWaitlist]);

  const handleDelete = async (entryId) => {
    setDeleting(entryId);
    try {
      await axios.delete(`${API}/api/admin/waitlist/${entryId}`);
      setEntries(prev => prev.filter(e => e.id !== entryId));
    } catch (e) {
      console.error("Delete failed", e);
    } finally {
      setDeleting(null);
    }
  };

  // Group entries by product
  const grouped = entries.reduce((acc, entry) => {
    const key = entry.product_id;
    if (!acc[key]) acc[key] = { product_name: entry.product_name, product_id: key, entries: [] };
    acc[key].entries.push(entry);
    return acc;
  }, {});

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#050505" }}>
        <div className="w-8 h-8 border border-[#39FF14] border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#050505" }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <p className="font-mono uppercase tracking-[0.3em] text-xs font-bold text-[#39FF14] mb-2">Admin</p>
            <h1 data-testid="admin-waitlist-heading" className="text-4xl sm:text-5xl font-black uppercase tracking-tighter text-[#F2F2F2]" style={{ fontFamily: "Chivo, sans-serif" }}>
              Waitlist Manager
            </h1>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => navigate("/admin")}
              variant="outline"
              className="bg-transparent border-[#1A1A1A] hover:border-[#39FF14] text-[#A1A1A1] hover:text-[#39FF14] rounded-none font-mono text-xs uppercase tracking-wider"
              data-testid="back-to-admin-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" strokeWidth={1.5} />
              Products
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-[1px] bg-[#1A1A1A] mb-8">
          <div className="bg-[#0D0D0D] p-4 sm:p-6">
            <p className="font-mono uppercase tracking-[0.2em] text-[10px] font-bold text-[#A1A1A1] mb-1">Total Entries</p>
            <p className="font-mono text-2xl font-bold text-[#39FF14]" data-testid="total-entries">{entries.length}</p>
          </div>
          <div className="bg-[#0D0D0D] p-4 sm:p-6">
            <p className="font-mono uppercase tracking-[0.2em] text-[10px] font-bold text-[#A1A1A1] mb-1">Products with Waitlists</p>
            <p className="font-mono text-2xl font-bold text-[#39FF14]" data-testid="total-products">{Object.keys(grouped).length}</p>
          </div>
          <div className="bg-[#0D0D0D] p-4 sm:p-6 col-span-2 sm:col-span-1">
            <p className="font-mono uppercase tracking-[0.2em] text-[10px] font-bold text-[#A1A1A1] mb-1">Unique Users</p>
            <p className="font-mono text-2xl font-bold text-[#39FF14]" data-testid="unique-users">{new Set(entries.map(e => e.user_id)).size}</p>
          </div>
        </div>

        {/* Waitlist by Product */}
        {Object.keys(grouped).length === 0 ? (
          <div className="bg-[#0D0D0D] border border-[#1A1A1A] p-12 text-center" data-testid="no-waitlist">
            <Clock className="h-12 w-12 text-[#1A1A1A] mx-auto mb-4" />
            <p className="text-[#A1A1A1] text-lg">No waitlist entries yet.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.values(grouped).map((group) => (
              <div key={group.product_id} className="border border-[#1A1A1A]" data-testid={`waitlist-group-${group.product_id}`}>
                {/* Product Header */}
                <div className="bg-[#0D0D0D] p-4 border-b border-[#1A1A1A] flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Users className="h-4 w-4 text-[#39FF14]" />
                    <Link to={`/products/${group.product_id}`} className="text-[#F2F2F2] font-bold text-sm hover:text-[#39FF14] transition-colors uppercase tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>
                      {group.product_name}
                    </Link>
                  </div>
                  <span className="font-mono text-xs text-[#39FF14]">{group.entries.length} waiting</span>
                </div>

                {/* Entries Table */}
                <div className="bg-[#0D0D0D]">
                  {/* Header */}
                  <div className="hidden sm:grid grid-cols-[60px_1fr_1fr_160px_80px] gap-4 p-3 border-b border-[#1A1A1A]">
                    <span className="font-mono uppercase tracking-[0.2em] text-[10px] font-bold text-[#39FF14]">#</span>
                    <span className="font-mono uppercase tracking-[0.2em] text-[10px] font-bold text-[#39FF14]">Name</span>
                    <span className="font-mono uppercase tracking-[0.2em] text-[10px] font-bold text-[#39FF14]">Email</span>
                    <span className="font-mono uppercase tracking-[0.2em] text-[10px] font-bold text-[#39FF14]">Joined</span>
                    <span className="font-mono uppercase tracking-[0.2em] text-[10px] font-bold text-[#39FF14]">Action</span>
                  </div>
                  {group.entries.map((entry, idx) => (
                    <div key={entry.id} data-testid={`admin-waitlist-entry-${entry.id}`} className="flex flex-col sm:grid sm:grid-cols-[60px_1fr_1fr_160px_80px] gap-2 sm:gap-4 p-3 border-b border-[#1A1A1A]/50 hover:bg-[#141414] transition-colors">
                      <span className="font-mono text-sm text-[#39FF14] font-bold">{idx + 1}</span>
                      <span className="text-[#F2F2F2] text-sm">{entry.user_name}</span>
                      <span className="text-[#A1A1A1] text-sm truncate">{entry.user_email}</span>
                      <span className="font-mono text-xs text-[#A1A1A1]">{new Date(entry.created_at).toLocaleString()}</span>
                      <button
                        onClick={() => handleDelete(entry.id)}
                        disabled={deleting === entry.id}
                        data-testid={`delete-waitlist-${entry.id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 border border-[#1A1A1A] hover:border-red-500 text-[#A1A1A1] hover:text-red-400 hover:bg-red-500/10 transition-colors font-mono text-[10px] uppercase tracking-wider disabled:opacity-50 w-fit"
                      >
                        <Trash2 className="h-3 w-3" />
                        {deleting === entry.id ? "..." : "Remove"}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
